<template>
  <div class="game-card" @click="handleGameClick">
    <!-- Background Image with Loading Placeholder -->
    <div class="game-background">
      <!-- Loading Placeholder -->
      <div v-if="!imageLoaded" class="image-placeholder">
        <div class="placeholder-shimmer" />
        <div class="placeholder-content">
          <div class="placeholder-icon">
            🎮
          </div>
          <div class="placeholder-text">
            Loading...
          </div>
        </div>
      </div>

      <!-- Actual Image -->
      <img
        :src="game.image.vertical"
        :alt="game.gameName"
        class="game-image"
        :class="{ 'image-loaded': imageLoaded }"
        loading="lazy"
        :sizes="imageSizes"
        @load="handleImageLoad"
        @error="handleImageError"
      />
    </div>

    <!-- Online Count - Top Right -->
    <div v-if="game.online > 0 && showOnlineCount" class="online-count">
      <span class="online-icon">👥</span>
      <span class="online-number">{{ formatOnlineCount(game.online) }}</span>
    </div>

    <!-- Content Overlay -->
    <div class="game-content">
      <!-- Row 1: Provider and Percentage -->
      <div class="content-row-1">
        <div class="game-provider">
          {{ game.provider }}
        </div>
        <div v-if="game.rtp && showPercentage" class="game-percentage">
          RTP: {{ Number(game.rtp).toFixed(1) }}%
        </div>
      </div>

      <!-- Row 2: Game Name -->
      <div class="content-row-2">
        <div class="game-name">
          {{ game.gameName }}
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLaunching" class="loading-overlay">
      <div class="loading-spinner" />
      <div class="loading-text">
        กำลังเปิดเกม...
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import authCheck from '@/mixins/authCheck'

export default {
  name: 'GameCard',
  mixins: [authCheck],
  props: {
    game: {
      type: Object,
      required: true
    },
    showOnlineCount: {
      type: Boolean,
      default: true
    },
    showPercentage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLaunching: false,
      imageLoaded: false
    }
  },
  computed: {
    ...mapState('member', ['isMockCredit', 'contactUrl']),
    ...mapGetters('member', ['isGameBlocked']),
    imageSizes() {
      return '(max-width: 480px) 140px, (max-width: 768px) 160px, 180px'
    }
  },
  emits: ['game-select', 'show-login-modal'],
  methods: {
    handleGameClick() {
      // Check authentication before allowing game selection
      this.requireAuth(() => {
        // Check if using mock credit (free promo user who hasn't deposited)
        if (this.isMockCredit) {
          this.$swal({
            title: 'รบกวนยืนยันบัญชีกับแอดมิน',
            text:
              'เครดิตฟรีอยู่ในกระเป๋าเรียบร้อยแล้ว เหลือเพียงแค่แจ้งเบอร์โทรยืนยันบัญชีค่าา',
            icon: 'warning',
            confirmButtonText: 'ติดต่อแอดมิน',
            customClass: {
              confirmButton: 'btn btn-primary'
            },
            buttonsStyling: false
          }).then(result => {
            if (result.isConfirmed && this.contactUrl) {
              window.open(this.contactUrl, '_blank')
            }
          })
          return
        }

        // Active promotion restricts this game — popup instead of launching.
        // Synchronous check against the Vuex snapshot keeps window.open inside
        // the click stack for allowed games; the server hard-block is the backstop.
        if (this.isGameBlocked(this.game.provider, this.game.id)) {
          this.$swal({
            title: 'ไม่สามารถเล่นเกมนี้ได้',
            text:
              'โปรโมชั่นที่คุณรับอยู่ไม่อนุญาตให้เล่นเกมนี้ กรุณาทำเทิร์นให้ครบหรือติดต่อแอดมิน',
            icon: 'warning',
            confirmButtonText: 'ตกลง',
            customClass: {
              confirmButton: 'btn btn-primary'
            },
            buttonsStyling: false
          })
          // Refresh the snapshot in the background for subsequent clicks
          this.$store.dispatch('member/fetchGameRestriction')
          return
        }

        this.launchGame()
      }, `launch-${this.game.gameName}`)
    },

    launchGame() {
      const { provider, id: gameId, providerType, gameCategory } = this.game

      // AML provider uses different API endpoint, keep same-window redirect
      if (provider === 'AML') {
        this.launchAMLGame()
        return
      }

      // Route-based launch in new tab for all other providers
      const tab = providerType || gameCategory || 'SLOT'
      const url = `${window.location.origin}/startgame?p=${encodeURIComponent(
        provider
      )}&g=${encodeURIComponent(gameId)}&t=${encodeURIComponent(tab)}`

      // Open in new tab - this works because it's synchronous on user click
      window.open(url, '_blank')

      // Emit to parent for any additional handling
      this.$emit('game-select', this.game)
    },

    async launchAMLGame() {
      try {
        this.isLaunching = true
        console.log('Launching AML/Lotto game:', this.game)

        const url = await this.$http.post('member/lotto/start', {
          page: 'home'
        })
        console.log('AML API response:', url.data)

        if (url.data.success === true && url.data.result?.uri) {
          // Navigate in same window for AML games
          window.location.href = url.data.result.uri
        } else {
          this.$swal({
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถเปิดเกมได้ กรุณาลองใหม่อีกครั้ง',
            icon: 'error',
            confirmButtonText: 'ตกลง',
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false
          })
        }
      } catch (error) {
        console.error('Failed to launch AML game:', error)
        this.$swal({
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถเปิดเกมได้ กรุณาลองใหม่อีกครั้ง',
          icon: 'error',
          confirmButtonText: 'ตกลง',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false
        })
      } finally {
        this.isLaunching = false
      }
    },

    handleImageLoad() {
      // Image loaded successfully - fade in the image
      this.imageLoaded = true
    },

    handleImageError(event) {
      // Handle image loading error by hiding the broken image
      const { target } = event
      target.style.display = 'none'
    },

    formatOnlineCount(count) {
      if (count >= 1000) {
        return `${Math.floor(count / 1000)}k+`
      }
      return count?.toString() || '0'
    }
  }
}
</script>

<style lang="scss" scoped>
.game-card {
  width: 100%;
  aspect-ratio: 3/4.1;
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.02);

  // Mobile performance optimizations
  transform: translate3d(0, 0, 0);
  will-change: transform;

  // Desktop hover effects
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translate3d(0, -4px, 0);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);

      .game-image {
        transform: scale(1.05);
        filter: brightness(1.1);
      }
    }
  }

  // Mobile optimizations
  @media (max-width: 768px) {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

    // Simplified hover for tablet
    @media (hover: hover) {
      &:hover {
        transform: translate3d(0, -2px, 0);
      }
    }
  }

  @media (max-width: 480px) {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);

    // No hover effects on mobile touch devices
    &:hover {
      transform: translate3d(0, 0, 0);
    }

    // Touch feedback instead
    &:active {
      transform: translate3d(0, -1px, 0) scale(0.98);
      transition: transform 0.1s ease;
    }
  }
}

.game-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.game-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.2s ease;
  filter: brightness(1.05) contrast(1);
  opacity: 0;
  transform: scale(1.02);

  // Performance optimizations for lazy loading
  contain: layout style paint;

  &.image-loaded {
    opacity: 1;
    transform: scale(1);
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  @media (max-width: 480px) {
    transition: opacity 0.2s ease;

    &.image-loaded {
      transition: opacity 0.2s ease;
    }
  }
}

// Fixed dark theme colors
$bg-card: #1a1a2e;
$bg-elevated: #16213e;

// Loading placeholder styles
.image-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, $bg-card, $bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  border-radius: 10px;
}

.placeholder-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  animation: shimmer 2s infinite;
  border-radius: 10px;
}

.placeholder-content {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  z-index: 3;
}

.placeholder-icon {
  font-size: clamp(1.5rem, 4vw, 2rem);
  margin-bottom: 8px;
  opacity: 0.7;
}

.placeholder-text {
  font-size: clamp(0.7rem, 1.5vw, 0.8rem);
  font-weight: 400;
  opacity: 0.8;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.game-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  z-index: 3;
  color: #fff;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.75) 60%,
    rgba(0, 0, 0, 0.5) 80%,
    transparent 100%
  );

  @media (max-width: 480px) {
    padding: 12px;
  }
}

.content-row-1 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
  gap: 8px;
}

.content-row-2 {
  margin-bottom: 0;
}

.game-provider {
  font-size: clamp(0.7rem, 1.5vw, 0.8rem);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.game-percentage {
  font-size: clamp(0.65rem, 1.3vw, 0.75rem);
  font-weight: 400;
  color: #f97878;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  background: rgba(48, 22, 23, 0.4);
  padding: 2px 6px;
  border-radius: 8px;
  border: 1px solid rgba(245, 179, 201, 0.8);
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: clamp(0.6rem, 3vw, 0.7rem);
    padding: 1px 4px;
    border-radius: 6px;
  }
}

.game-name {
  font-size: clamp(0.9rem, 2vw, 1.1rem);
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.online-count {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: clamp(0.7rem, 1.4vw, 0.8rem);
  font-weight: 700;
  color: #fff;
  background: rgba(0, 0, 0, 0.8);
  padding: 6px 10px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 4;

  .online-icon {
    font-size: 0.85em;
    opacity: 0.9;
  }

  .online-number {
    font-variant-numeric: tabular-nums;
    color: #f5deb3;
    font-weight: 400;
  }

  @media (max-width: 480px) {
    top: 8px;
    right: 8px;
    padding: 4px 8px;
    font-size: clamp(0.65rem, 3vw, 0.75rem);
    border-radius: 10px;
  }
}

// Loading overlay styles
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  z-index: 10;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(var(--theme-secondary-rgb), 0.3);
  border-top: 3px solid var(--theme-secondary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

.loading-text {
  color: var(--theme-secondary-light);
  font-size: 0.8rem;
  font-weight: 400;
  text-align: center;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Touch device optimizations for Android performance
@media (hover: none) and (pointer: coarse) {
  .game-card {
    will-change: auto; // Reduce GPU memory usage on touch devices
    transition: transform 0.1s ease;

    &:active {
      transform: scale(0.98);
    }
  }

  .game-image {
    will-change: auto;
    transition: opacity 0.15s ease;

    &.image-loaded {
      transition: opacity 0.15s ease;
    }
  }

  .placeholder-shimmer {
    animation-duration: 3s; // Slower animation = less CPU usage
  }
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
  .game-card,
  .game-image {
    transition: none;
  }

  .placeholder-shimmer {
    animation: none;
  }

  .loading-spinner {
    animation-duration: 2s;
  }
}
</style>
